import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store';
import { api } from '../utils/api';
import { ChevronLeft, ChevronRight, CheckCircle, Clock, Shuffle } from 'lucide-react';

interface Question {
  unique_id: string;
  level: number;
  part: string;
  question_text: string;
  options: string[];
  correct_option_index: number;
  explanation?: string;
  difficulty?: string;
  tags?: string[];
}

interface RandomTestData {
  test_session_id: string;
  questions: Question[];
}

const RandomTest: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { token } = useAuthStore();
  
  const [testData, setTestData] = useState<RandomTestData | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<{ [key: number]: number | null }>({});
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(30 * 60); // 30 minutes
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!location.state?.testData) {
      navigate('/');
      return;
    }
    setTestData(location.state.testData);
  }, [location.state, navigate]);

  useEffect(() => {
    if (testData && currentQuestionIndex < testData.questions.length) {
      setSelectedAnswer(answers[currentQuestionIndex] || null);
    }
  }, [currentQuestionIndex, answers, testData]);

  useEffect(() => {
    if (timeRemaining > 0 && !showResults) {
      const timer = setTimeout(() => setTimeRemaining(timeRemaining - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeRemaining === 0) {
      handleSubmitTest();
    }
  }, [timeRemaining, showResults]);

  const handleAnswer = (answerIndex: number) => {
    setSelectedAnswer(answerIndex);
    setAnswers(prev => ({
      ...prev,
      [currentQuestionIndex]: answerIndex
    }));
  };

  const handleDontKnow = () => {
    setSelectedAnswer(-1);
    setAnswers(prev => ({
      ...prev,
      [currentQuestionIndex]: -1
    }));
  };

  const nextQuestion = () => {
    if (testData && currentQuestionIndex < testData.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const previousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleSubmitTest = async () => {
    if (!testData || !token) return;

    setLoading(true);
    try {
      const answersList = testData.questions.map((question, index) => ({
        question_id: question.unique_id,
        chosen_option_index: answers[index] === -1 || answers[index] === undefined ? null : answers[index],
        chose_buzhidao: answers[index] === -1
      }));

      await api.submitTest(testData.test_session_id, answersList, token);
      setShowResults(true);
    } catch (error) {
      console.error('Error submitting test:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateScore = () => {
    if (!testData) return { correct: 0, total: 0, percentage: 0 };
    
    let correct = 0;
    testData.questions.forEach((question, index) => {
      if (answers[index] === question.correct_option_index) {
        correct++;
      }
    });
    
    return {
      correct,
      total: testData.questions.length,
      percentage: Math.round((correct / testData.questions.length) * 100)
    };
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  if (!testData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Shuffle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Loading random test...</p>
        </div>
      </div>
    );
  }

  if (showResults) {
    const score = calculateScore();
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Random Test Complete!</h2>
          <div className="text-4xl font-bold text-blue-600 mb-2">{score.percentage}%</div>
          <p className="text-gray-600 mb-6">
            You got {score.correct} out of {score.total} questions correct
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{score.correct}</div>
              <div className="text-blue-800">Correct</div>
            </div>
            <div className="bg-red-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-red-600">{score.total - score.correct}</div>
              <div className="text-red-800">Incorrect</div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-gray-600">{score.total}</div>
              <div className="text-gray-800">Total</div>
            </div>
          </div>

          <div className="space-x-4">
            <button
              onClick={() => navigate('/')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md font-medium transition-colors"
            >
              Back to Dashboard
            </button>
            <button
              onClick={() => window.location.reload()}
              className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-2 rounded-md font-medium transition-colors"
            >
              Take Another Random Test
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentQuestion = testData.questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / testData.questions.length) * 100;

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Shuffle className="w-6 h-6 text-orange-600" />
            <h1 className="text-xl font-semibold text-gray-900">Random HSK Test</h1>
            <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded text-sm">
              HSK {currentQuestion.level} - {currentQuestion.part}
            </span>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center text-gray-600">
              <Clock className="w-4 h-4 mr-1" />
              <span className={timeRemaining < 300 ? 'text-red-600 font-semibold' : ''}>
                {formatTime(timeRemaining)}
              </span>
            </div>
            <span className="text-sm text-gray-600">
              {currentQuestionIndex + 1} / {testData.questions.length}
            </span>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="mt-4">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-orange-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Question */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="mb-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            Question {currentQuestionIndex + 1}
          </h2>
          <p className="text-xl text-gray-800 leading-relaxed">
            {currentQuestion.question_text}
          </p>
        </div>

        {/* Options */}
        <div className="space-y-3 mb-6">
          {currentQuestion.options.map((option, index) => (
            <button
              key={index}
              onClick={() => handleAnswer(index)}
              className={`w-full text-left p-4 rounded-lg border-2 transition-colors ${
                selectedAnswer === index
                  ? 'border-orange-500 bg-orange-50'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <span className="font-medium text-gray-700 mr-3">
                {String.fromCharCode(65 + index)}.
              </span>
              <span className="text-gray-900">{option}</span>
            </button>
          ))}
        </div>

        {/* Don't Know Button */}
        <button
          onClick={handleDontKnow}
          className={`w-full p-3 rounded-lg border-2 transition-colors mb-6 ${
            selectedAnswer === -1
              ? 'border-gray-500 bg-gray-100'
              : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
          }`}
        >
          <span className="text-gray-700 font-medium">不知道 (Don't know)</span>
        </button>
      </div>

      {/* Navigation */}
      <div className="flex justify-between items-center">
        <button
          onClick={previousQuestion}
          disabled={currentQuestionIndex === 0}
          className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Previous
        </button>

        <div className="flex space-x-3">
          {currentQuestionIndex === testData.questions.length - 1 ? (
            <button
              onClick={handleSubmitTest}
              disabled={loading}
              className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-6 py-2 rounded-md font-medium transition-colors flex items-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Submitting...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Submit Test
                </>
              )}
            </button>
          ) : (
            <button
              onClick={nextQuestion}
              className="flex items-center px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-md font-medium transition-colors"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RandomTest;